import React, { useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { extractErrorMessage } from '../../utils/errorHandler'

interface SkillCategory {
  category_id: string
  category_name: string
  category_description: string
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface SkillItem {
  item_id: string
  category_id: string
  category_name: string
  item_name: string
  item_description: string
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface SkillLevelDefinition {
  id?: number
  category_id?: string | null
  category_name?: string
  level: string
  level_name: string
  level_description: string
  achievement_criteria: string
  display_order: number
  is_default?: boolean
  created_at: string
  updated_at: string
}

interface CertificationMaster {
  certification_id: string
  certification_name: string
  issuing_organization: string
  has_expiry: boolean
  expiry_period_months: number | null
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface CategoryFormData {
  category_id: string
  category_name: string
  category_description: string
  display_order: number
}

interface ItemFormData {
  item_id: string
  category_id: string
  item_name: string
  item_description: string
  display_order: number
}

interface LevelDefinitionFormData {
  category_id?: string | null
  level: string
  level_name: string
  level_description: string
  achievement_criteria: string
  years_of_experience: number
  display_order: number
}

interface CertificationFormData {
  certification_id: string
  certification_name: string
  issuing_organization: string
  has_expiry: boolean
  expiry_period_months: number | null
  display_order: number
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`skill-master-tabpanel-${index}`}
      aria-labelledby={`skill-master-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

const SkillMasterManagementPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [tabValue, setTabValue] = useState(0)
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [itemDialogOpen, setItemDialogOpen] = useState(false)
  const [levelDialogOpen, setLevelDialogOpen] = useState(false)
  const [certificationDialogOpen, setCertificationDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<SkillCategory | null>(null)
  const [editingItem, setEditingItem] = useState<SkillItem | null>(null)
  const [editingLevel, setEditingLevel] = useState<SkillLevelDefinition | null>(null)
  const [editingCertification, setEditingCertification] = useState<CertificationMaster | null>(null)
  const [selectedCategoryForLevel, setSelectedCategoryForLevel] = useState<string | null>(null)
  const [batchEditDialogOpen, setBatchEditDialogOpen] = useState(false)
  const [batchEditData, setBatchEditData] = useState<SkillLevelDefinition[]>([])

  const {
    control: categoryControl,
    handleSubmit: handleCategorySubmit,
    reset: resetCategory,
    formState: { errors: categoryErrors },
  } = useForm<CategoryFormData>({
    defaultValues: {
      category_id: '',
      category_name: '',
      category_description: '',
      display_order: 1,
    },
  })

  const {
    control: itemControl,
    handleSubmit: handleItemSubmit,
    reset: resetItem,
    formState: { errors: itemErrors },
  } = useForm<ItemFormData>({
    defaultValues: {
      item_id: '',
      category_id: '',
      item_name: '',
      item_description: '',
      display_order: 1,
    },
  })

  const {
    control: levelControl,
    handleSubmit: handleLevelSubmit,
    reset: resetLevel,
    formState: { errors: levelErrors },
  } = useForm<LevelDefinitionFormData>({
    defaultValues: {
      level: '',
      level_name: '',
      level_description: '',
      achievement_criteria: '',
      years_of_experience: 0,
      display_order: 1,
    },
  })

  const {
    control: certificationControl,
    handleSubmit: handleCertificationSubmit,
    reset: resetCertification,
    watch: watchCertification,
    formState: { errors: certificationErrors },
  } = useForm<CertificationFormData>({
    defaultValues: {
      certification_id: '',
      certification_name: '',
      issuing_organization: '',
      has_expiry: false,
      expiry_period_months: null,
      display_order: 1,
    },
  })

  const hasExpiry = watchCertification('has_expiry')

  // Fetch skill categories
  const { data: categoriesData, isLoading: categoriesLoading, refetch: refetchCategories } = useQuery({
    queryKey: ['skill-categories'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: SkillCategory[] }>('/skills/categories?is_active=true')
      return response.data.data
    },
  })

  // Fetch skill items
  const { data: itemsData, isLoading: itemsLoading, refetch: refetchItems } = useQuery({
    queryKey: ['skill-items'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: { items: SkillItem[] } }>('/skills/items?is_active=true&limit=1000')
      return response.data.data.items
    },
  })

  // Fetch skill level definitions (category-specific or default)
  const { data: categoryLevelsData, isLoading: levelsLoading, refetch: refetchLevels } = useQuery({
    queryKey: ['skill-level-definitions', selectedCategoryForLevel],
    queryFn: async () => {
      const params = selectedCategoryForLevel ? `?category_id=${selectedCategoryForLevel}` : ''
      const response = await api.get<{ success: boolean; data: SkillLevelDefinition[] }>(`/skills/level-definitions${params}`)
      return response.data.data
    },
  })

  // Fetch certification master
  const { data: certificationsData, isLoading: certificationsLoading, refetch: refetchCertifications } = useQuery({
    queryKey: ['certification-master'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: CertificationMaster[] }>('/skills/certifications?is_active=true')
      return response.data.data
    },
  })

  // Create/Update category mutation
  const saveCategoryMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      if (editingCategory) {
        return api.put(`/skills/categories/${editingCategory.category_id}`, data)
      } else {
        return api.post('/skills/categories', data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-categories'] })
      toast.success(editingCategory ? 'カテゴリを更新しました' : 'カテゴリを作成しました')
      setCategoryDialogOpen(false)
      setEditingCategory(null)
      resetCategory()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // Create/Update item mutation
  const saveItemMutation = useMutation({
    mutationFn: async (data: ItemFormData) => {
      if (editingItem) {
        return api.put(`/skills/items/${editingItem.item_id}`, data)
      } else {
        return api.post('/skills/items', data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-items'] })
      toast.success(editingItem ? 'スキル項目を更新しました' : 'スキル項目を作成しました')
      setItemDialogOpen(false)
      setEditingItem(null)
      resetItem()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // Create/Update level definition mutation
  const saveLevelMutation = useMutation({
    mutationFn: async (data: LevelDefinitionFormData) => {
      if (editingLevel) {
        return api.put(`/skills/level-definitions/${editingLevel.level}`, data)
      } else {
        return api.post('/skills/level-definitions', data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-level-definitions'] })
      toast.success(editingLevel ? 'レベル定義を更新しました' : 'レベル定義を作成しました')
      setLevelDialogOpen(false)
      setEditingLevel(null)
      resetLevel()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // Create/Update certification mutation
  const saveCertificationMutation = useMutation({
    mutationFn: async (data: CertificationFormData) => {
      if (editingCertification) {
        return api.put(`/skills/certifications/${editingCertification.certification_id}`, data)
      } else {
        return api.post('/skills/certifications', data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification-master'] })
      toast.success(editingCertification ? '資格マスタを更新しました' : '資格マスタを作成しました')
      setCertificationDialogOpen(false)
      setEditingCertification(null)
      resetCertification()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      return api.delete(`/skills/categories/${categoryId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-categories'] })
      toast.success('カテゴリを削除しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // Delete item mutation
  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      return api.delete(`/skills/items/${itemId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-items'] })
      toast.success('スキル項目を削除しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // Delete level definition mutation
  const deleteLevelMutation = useMutation({
    mutationFn: async (level: string) => {
      return api.delete(`/skills/level-definitions/${level}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-level-definitions'] })
      toast.success('レベル定義を削除しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // Delete certification mutation
  const deleteCertificationMutation = useMutation({
    mutationFn: async (certificationId: string) => {
      return api.delete(`/skills/certifications/${certificationId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification-master'] })
      toast.success('資格マスタを削除しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  const handleOpenCategoryDialog = (category?: SkillCategory) => {
    if (category) {
      setEditingCategory(category)
      resetCategory({
        category_id: category.category_id,
        category_name: category.category_name,
        category_description: category.category_description,
        display_order: category.display_order,
      })
    } else {
      setEditingCategory(null)
      resetCategory({
        category_id: '',
        category_name: '',
        category_description: '',
        display_order: (categoriesData?.length || 0) + 1,
      })
    }
    setCategoryDialogOpen(true)
  }

  const handleOpenItemDialog = (item?: SkillItem) => {
    if (item) {
      setEditingItem(item)
      resetItem({
        item_id: item.item_id,
        category_id: item.category_id,
        item_name: item.item_name,
        item_description: item.item_description,
        display_order: item.display_order,
      })
    } else {
      setEditingItem(null)
      resetItem({
        item_id: '',
        category_id: categoriesData?.[0]?.category_id || '',
        item_name: '',
        item_description: '',
        display_order: (itemsData?.length || 0) + 1,
      })
    }
    setItemDialogOpen(true)
  }

  const handleOpenLevelDialog = (level?: SkillLevelDefinition) => {
    if (level) {
      setEditingLevel(level)
      resetLevel({
        category_id: level.category_id,
        level: level.level,
        level_name: level.level_name,
        level_description: level.level_description,
        achievement_criteria: level.achievement_criteria,
        display_order: level.display_order,
      })
    } else {
      setEditingLevel(null)
      resetLevel({
        category_id: selectedCategoryForLevel,
        level: '',
        level_name: '',
        level_description: '',
        achievement_criteria: '',
        display_order: (categoryLevelsData?.length || 0) + 1,
      })
    }
    setLevelDialogOpen(true)
  }

  // バッチ更新機能
  const handleBatchUpdateLevels = () => {
    if (categoryLevelsData) {
      setBatchEditData([...categoryLevelsData])
    }
    setBatchEditDialogOpen(true)
  }

  // バッチ更新ミューテーション
  const batchUpdateLevelsMutation = useMutation({
    mutationFn: async (definitions: SkillLevelDefinition[]) => {
      const categoryId = selectedCategoryForLevel || 'null'
      return api.put(`/skills/level-definitions/category/${categoryId}/batch`, { definitions })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skill-level-definitions'] })
      toast.success('レベル定義を一括更新しました')
      setBatchEditDialogOpen(false)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  const handleOpenCertificationDialog = (certification?: CertificationMaster) => {
    if (certification) {
      setEditingCertification(certification)
      resetCertification({
        certification_id: certification.certification_id,
        certification_name: certification.certification_name,
        issuing_organization: certification.issuing_organization,
        has_expiry: certification.has_expiry,
        expiry_period_months: certification.expiry_period_months,
        display_order: certification.display_order,
      })
    } else {
      setEditingCertification(null)
      resetCertification({
        certification_id: '',
        certification_name: '',
        issuing_organization: '',
        has_expiry: false,
        expiry_period_months: null,
        display_order: (certificationsData?.length || 0) + 1,
      })
    }
    setCertificationDialogOpen(true)
  }

  const handleDeleteCategory = (category: SkillCategory) => {
    if (window.confirm(`カテゴリ「${category.category_name}」を削除しますか？`)) {
      deleteCategoryMutation.mutate(category.category_id)
    }
  }

  const handleDeleteItem = (item: SkillItem) => {
    if (window.confirm(`スキル項目「${item.item_name}」を削除しますか？`)) {
      deleteItemMutation.mutate(item.item_id)
    }
  }

  const handleDeleteLevel = (level: SkillLevelDefinition) => {
    if (window.confirm(`レベル定義「${level.level_name}」を削除しますか？`)) {
      deleteLevelMutation.mutate(level.level)
    }
  }

  const handleDeleteCertification = (certification: CertificationMaster) => {
    if (window.confirm(`資格マスタ「${certification.certification_name}」を削除しますか？`)) {
      deleteCertificationMutation.mutate(certification.certification_id)
    }
  }

  const onCategorySubmit = (data: CategoryFormData) => {
    saveCategoryMutation.mutate(data)
  }

  const onItemSubmit = (data: ItemFormData) => {
    saveItemMutation.mutate(data)
  }

  const onLevelSubmit = (data: LevelDefinitionFormData) => {
    saveLevelMutation.mutate(data)
  }

  const onCertificationSubmit = (data: CertificationFormData) => {
    saveCertificationMutation.mutate(data)
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          スキルマスタ管理
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            refetchCategories()
            refetchItems()
            refetchLevels()
            refetchCertifications()
          }}
        >
          更新
        </Button>
      </Box>

      <Paper>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="カテゴリ管理" />
          <Tab label="スキル項目管理" />
          <Tab label="レベル定義管理" />
          <Tab label="資格マスタ管理" />
        </Tabs>

        {/* Tab 0: カテゴリ管理 */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCategoryDialog()}
            >
              カテゴリ追加
            </Button>
          </Box>

          {categoriesLoading ? (
            <Typography>読み込み中...</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>カテゴリID</TableCell>
                    <TableCell>カテゴリ名</TableCell>
                    <TableCell>説明</TableCell>
                    <TableCell>表示順</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoriesData?.map((category) => (
                    <TableRow key={category.category_id}>
                      <TableCell>{category.category_id}</TableCell>
                      <TableCell>{category.category_name}</TableCell>
                      <TableCell>{category.category_description}</TableCell>
                      <TableCell>{category.display_order}</TableCell>
                      <TableCell>
                        <Chip
                          label={category.is_active ? '有効' : '無効'}
                          color={category.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCategoryDialog(category)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCategory(category)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab 1: スキル項目管理 */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenItemDialog()}
            >
              スキル項目追加
            </Button>
          </Box>

          {itemsLoading ? (
            <Typography>読み込み中...</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>項目ID</TableCell>
                    <TableCell>カテゴリ</TableCell>
                    <TableCell>項目名</TableCell>
                    <TableCell>説明</TableCell>
                    <TableCell>表示順</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemsData?.map((item) => (
                    <TableRow key={item.item_id}>
                      <TableCell>{item.item_id}</TableCell>
                      <TableCell>{item.category_name}</TableCell>
                      <TableCell>{item.item_name}</TableCell>
                      <TableCell>{item.item_description}</TableCell>
                      <TableCell>{item.display_order}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.is_active ? '有効' : '無効'}
                          color={item.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenItemDialog(item)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteItem(item)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab 2: レベル定義管理 */}
        <TabPanel value={tabValue} index={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            カテゴリごとにスキルレベルの定義をカスタマイズできます。デフォルト定義は全カテゴリ共通の基本定義です。
          </Alert>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>カテゴリ選択</InputLabel>
              <Select
                value={selectedCategoryForLevel || 'default'}
                onChange={(e) => setSelectedCategoryForLevel(e.target.value === 'default' ? null : e.target.value)}
                label="カテゴリ選択"
              >
                <MenuItem value="default">
                  <strong>デフォルト定義（全カテゴリ共通）</strong>
                </MenuItem>
                {categoriesData?.map((cat) => (
                  <MenuItem key={cat.category_id} value={cat.category_id}>
                    {cat.category_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedCategoryForLevel ?
                `カテゴリ: ${categoriesData?.find(c => c.category_id === selectedCategoryForLevel)?.category_name}` :
                'デフォルトレベル定義'}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => refetchLevels()}
                sx={{ mr: 1 }}
              >
                更新
              </Button>
              <Button
                variant="contained"
                onClick={() => handleBatchUpdateLevels()}
              >
                一括編集
              </Button>
            </Box>
          </Box>

          {levelsLoading ? (
            <Typography>読み込み中...</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="80">レベル</TableCell>
                    <TableCell width="150">レベル名</TableCell>
                    <TableCell>説明</TableCell>
                    <TableCell>達成基準</TableCell>
                    <TableCell width="80">表示順</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryLevelsData?.map((level) => (
                    <TableRow key={level.level}>
                      <TableCell>
                        <Chip
                          label={level.level}
                          color={level.level === 'S' ? 'secondary' :
                                 level.level === 'A' ? 'primary' :
                                 level.level === 'B' ? 'info' :
                                 level.level === 'C' ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {level.level_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2">
                          {level.level_description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 350 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
                          {level.achievement_criteria}
                        </Typography>
                      </TableCell>
                      <TableCell>{level.display_order}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab 3: 資格マスタ管理 */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCertificationDialog()}
            >
              資格マスタ追加
            </Button>
          </Box>

          {certificationsLoading ? (
            <Typography>読み込み中...</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>資格ID</TableCell>
                    <TableCell>資格名</TableCell>
                    <TableCell>発行機関</TableCell>
                    <TableCell>有効期限</TableCell>
                    <TableCell>期限(月)</TableCell>
                    <TableCell>表示順</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {certificationsData?.map((cert) => (
                    <TableRow key={cert.certification_id}>
                      <TableCell>{cert.certification_id}</TableCell>
                      <TableCell>{cert.certification_name}</TableCell>
                      <TableCell>{cert.issuing_organization}</TableCell>
                      <TableCell>
                        <Chip
                          label={cert.has_expiry ? 'あり' : 'なし'}
                          color={cert.has_expiry ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{cert.expiry_period_months || '-'}</TableCell>
                      <TableCell>{cert.display_order}</TableCell>
                      <TableCell>
                        <Chip
                          label={cert.is_active ? '有効' : '無効'}
                          color={cert.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCertificationDialog(cert)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCertification(cert)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? 'カテゴリ編集' : 'カテゴリ追加'}</DialogTitle>
        <form onSubmit={handleCategorySubmit(onCategorySubmit)}>
          <DialogContent>
            <Controller
              name="category_id"
              control={categoryControl}
              rules={{ required: 'カテゴリIDは必須です' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="カテゴリID"
                  fullWidth
                  margin="normal"
                  error={!!categoryErrors.category_id}
                  helperText={categoryErrors.category_id?.message}
                  disabled={!!editingCategory}
                />
              )}
            />
            <Controller
              name="category_name"
              control={categoryControl}
              rules={{ required: 'カテゴリ名は必須です' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="カテゴリ名"
                  fullWidth
                  margin="normal"
                  error={!!categoryErrors.category_name}
                  helperText={categoryErrors.category_name?.message}
                />
              )}
            />
            <Controller
              name="category_description"
              control={categoryControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="説明"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                />
              )}
            />
            <Controller
              name="display_order"
              control={categoryControl}
              rules={{ required: '表示順は必須です', min: { value: 1, message: '1以上の数値を入力してください' } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
                  label="表示順"
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!categoryErrors.display_order}
                  helperText={categoryErrors.display_order?.message}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryDialogOpen(false)}>キャンセル</Button>
            <Button type="submit" variant="contained">
              {editingCategory ? '更新' : '作成'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Item Dialog */}
      <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'スキル項目編集' : 'スキル項目追加'}</DialogTitle>
        <form onSubmit={handleItemSubmit(onItemSubmit)}>
          <DialogContent>
            <Controller
              name="item_id"
              control={itemControl}
              rules={{ required: 'スキル項目IDは必須です' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="スキル項目ID"
                  fullWidth
                  margin="normal"
                  error={!!itemErrors.item_id}
                  helperText={itemErrors.item_id?.message}
                  disabled={!!editingItem}
                />
              )}
            />
            <Controller
              name="category_id"
              control={itemControl}
              rules={{ required: 'カテゴリは必須です' }}
              render={({ field }) => (
                <FormControl fullWidth margin="normal" error={!!itemErrors.category_id}>
                  <InputLabel>カテゴリ</InputLabel>
                  <Select {...field} label="カテゴリ">
                    {categoriesData?.map((cat) => (
                      <MenuItem key={cat.category_id} value={cat.category_id}>
                        {cat.category_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {itemErrors.category_id && (
                    <FormHelperText>{itemErrors.category_id.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name="item_name"
              control={itemControl}
              rules={{ required: 'スキル項目名は必須です' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="スキル項目名"
                  fullWidth
                  margin="normal"
                  error={!!itemErrors.item_name}
                  helperText={itemErrors.item_name?.message}
                />
              )}
            />
            <Controller
              name="item_description"
              control={itemControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="説明"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                />
              )}
            />
            <Controller
              name="display_order"
              control={itemControl}
              rules={{ required: '表示順は必須です', min: { value: 1, message: '1以上の数値を入力してください' } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
                  label="表示順"
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!itemErrors.display_order}
                  helperText={itemErrors.display_order?.message}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setItemDialogOpen(false)}>キャンセル</Button>
            <Button type="submit" variant="contained">
              {editingItem ? '更新' : '作成'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Level Definition Dialog */}
      <Dialog open={levelDialogOpen} onClose={() => setLevelDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingLevel ? 'レベル定義編集' : 'レベル定義追加'}</DialogTitle>
        <form onSubmit={handleLevelSubmit(onLevelSubmit)}>
          <DialogContent>
            <Controller
              name="level"
              control={levelControl}
              rules={{
                required: 'レベルコードは必須です',
                pattern: { value: /^[SABCD]$/, message: 'S, A, B, C, Dのいずれかを入力してください' },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="レベルコード (S/A/B/C/D)"
                  fullWidth
                  margin="normal"
                  error={!!levelErrors.level}
                  helperText={levelErrors.level?.message}
                  disabled={!!editingLevel}
                  inputProps={{ maxLength: 1 }}
                />
              )}
            />
            <Controller
              name="level_name"
              control={levelControl}
              rules={{ required: 'レベル名は必須です' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="レベル名"
                  fullWidth
                  margin="normal"
                  error={!!levelErrors.level_name}
                  helperText={levelErrors.level_name?.message}
                />
              )}
            />
            <Controller
              name="level_description"
              control={levelControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="説明文"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                />
              )}
            />
            <Controller
              name="achievement_criteria"
              control={levelControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="達成基準・例（改行で複数入力可）"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                  helperText="例: ・複雑な課題を独力で解決できる\n・チームメンバーへの技術指導が可能"
                />
              )}
            />
            <Controller
              name="years_of_experience"
              control={levelControl}
              rules={{ required: '経験年数は必須です', min: { value: 0, message: '0以上の数値を入力してください' } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="想定経験年数"
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!levelErrors.years_of_experience}
                  helperText={levelErrors.years_of_experience?.message}
                />
              )}
            />
            <Controller
              name="display_order"
              control={levelControl}
              rules={{ required: '表示順は必須です', min: { value: 1, message: '1以上の数値を入力してください' } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
                  label="表示順"
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!levelErrors.display_order}
                  helperText={levelErrors.display_order?.message}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLevelDialogOpen(false)}>キャンセル</Button>
            <Button type="submit" variant="contained">
              {editingLevel ? '更新' : '作成'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Certification Dialog */}
      <Dialog open={certificationDialogOpen} onClose={() => setCertificationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCertification ? '資格マスタ編集' : '資格マスタ追加'}</DialogTitle>
        <form onSubmit={handleCertificationSubmit(onCertificationSubmit)}>
          <DialogContent>
            <Controller
              name="certification_id"
              control={certificationControl}
              rules={{ required: '資格IDは必須です' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="資格ID"
                  fullWidth
                  margin="normal"
                  error={!!certificationErrors.certification_id}
                  helperText={certificationErrors.certification_id?.message}
                  disabled={!!editingCertification}
                />
              )}
            />
            <Controller
              name="certification_name"
              control={certificationControl}
              rules={{ required: '資格名は必須です' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="資格名"
                  fullWidth
                  margin="normal"
                  error={!!certificationErrors.certification_name}
                  helperText={certificationErrors.certification_name?.message}
                />
              )}
            />
            <Controller
              name="issuing_organization"
              control={certificationControl}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="発行機関"
                  fullWidth
                  margin="normal"
                />
              )}
            />
            <Controller
              name="has_expiry"
              control={certificationControl}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="有効期限あり"
                  sx={{ mt: 2 }}
                />
              )}
            />
            {hasExpiry && (
              <Controller
                name="expiry_period_months"
                control={certificationControl}
                rules={
                  hasExpiry
                    ? {
                        required: '有効期限ありの場合、期限(月数)は必須です',
                        min: { value: 1, message: '1以上の数値を入力してください' },
                      }
                    : {}
                }
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                    label="有効期限(月数)"
                    type="number"
                    fullWidth
                    margin="normal"
                    error={!!certificationErrors.expiry_period_months}
                    helperText={certificationErrors.expiry_period_months?.message}
                  />
                )}
              />
            )}
            <Controller
              name="display_order"
              control={certificationControl}
              rules={{ required: '表示順は必須です', min: { value: 1, message: '1以上の数値を入力してください' } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
                  label="表示順"
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!certificationErrors.display_order}
                  helperText={certificationErrors.display_order?.message}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCertificationDialogOpen(false)}>キャンセル</Button>
            <Button type="submit" variant="contained">
              {editingCertification ? '更新' : '作成'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Batch Edit Level Definitions Dialog */}
      <Dialog open={batchEditDialogOpen} onClose={() => setBatchEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCategoryForLevel ?
            `レベル定義一括編集: ${categoriesData?.find(c => c.category_id === selectedCategoryForLevel)?.category_name}` :
            'デフォルトレベル定義一括編集'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            5つのレベル（S, A, B, C, D）の定義を一括で編集できます。
          </Alert>
          {batchEditData?.map((levelDef, index) => (
            <Box key={levelDef.level} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                レベル {levelDef.level}
              </Typography>
              <TextField
                label="レベル名"
                fullWidth
                margin="normal"
                value={levelDef.level_name}
                onChange={(e) => {
                  const newData = [...batchEditData]
                  newData[index] = { ...newData[index], level_name: e.target.value }
                  setBatchEditData(newData)
                }}
              />
              <TextField
                label="説明"
                fullWidth
                margin="normal"
                multiline
                rows={2}
                value={levelDef.level_description}
                onChange={(e) => {
                  const newData = [...batchEditData]
                  newData[index] = { ...newData[index], level_description: e.target.value }
                  setBatchEditData(newData)
                }}
              />
              <TextField
                label="達成基準"
                fullWidth
                margin="normal"
                multiline
                rows={3}
                value={levelDef.achievement_criteria}
                onChange={(e) => {
                  const newData = [...batchEditData]
                  newData[index] = { ...newData[index], achievement_criteria: e.target.value }
                  setBatchEditData(newData)
                }}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchEditDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (batchEditData.length > 0) {
                batchUpdateLevelsMutation.mutate(batchEditData)
              }
            }}
            disabled={batchUpdateLevelsMutation.isPending}
          >
            {batchUpdateLevelsMutation.isPending ? '更新中...' : '一括更新'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SkillMasterManagementPage